import React, { useState, useMemo } from 'react';
import { formatDate } from '../../utils/helpers';
import { sanitizeHTMLForDisplay } from '../../utils/htmlSanitizer';
import ImageLightboxModal from '../common/ImageLightboxModal';
import { normalizeGalleryImages } from '../../utils/imageLightbox';
import '../common/RichTextEditor.css';

const PostDetailView = ({
  post,
  comments = [],
  commentsLoading = false,
  isOwner,
  onEdit,
  onDelete,
  onLike,
  onShare,
  onSubmitComment,
  showCommentForm,
  setShowCommentForm,
  newComment,
  setNewComment,
  sendingComment
}) => {
  const [openIndex, setOpenIndex] = useState(null);
  const gallerySlides = useMemo(
    () => normalizeGalleryImages(post?.images, 'Post'),
    [post?.images],
  );

  const openImageModal = (index) => setOpenIndex(index);
  const closeImageModal = () => setOpenIndex(null);

  const getCategoryColor = (category) => {
    const colors = {
      news: 'bg-blue-100 text-blue-800 border-blue-200',
      technology: 'bg-purple-100 text-purple-800 border-purple-200',
      business: 'bg-green-100 text-green-800 border-green-200',
      health: 'bg-red-100 text-red-800 border-red-200',
      lifestyle: 'bg-pink-100 text-pink-800 border-pink-200',
      entertainment: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      sports: 'bg-orange-100 text-orange-800 border-orange-200',
      education: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      opinion: 'bg-teal-100 text-teal-800 border-teal-200',
      qa: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      travel: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      visa: 'bg-sky-100 text-sky-800 border-sky-200',
      recipes: 'bg-amber-100 text-amber-800 border-amber-200',
      announcements: 'bg-violet-100 text-violet-800 border-violet-200',
      local: 'bg-lime-100 text-lime-800 border-lime-200',
      deals: 'bg-rose-100 text-rose-800 border-rose-200',
      other: 'bg-slate-100 text-slate-800 border-slate-200',
    };
    return colors[category] || colors.other;
  };

  return (
    <article className="max-w-4xl mx-auto">
      {/* Hero Image Section */}
      {post.images && post.images.length > 0 && (
        <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl cursor-pointer group" onClick={() => openImageModal(0)}>
          <div className="aspect-video w-full">
            <img
              src={post.images[0].original_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          {/* Zoom indicator overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="bg-black/50 backdrop-blur-sm rounded-full p-2">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          
          {/* Floating Content Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="flex items-end justify-between">
              <div className="text-white">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                    {post.user_avatar ? (
                      <img
                        src={post.user_avatar}
                        alt={post.user_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{post.user_name || 'Unknown User'}</p>
                    <p className="text-white/80 text-sm">{formatDate(post.created_at)}</p>
                  </div>
                </div>
                <h1 className="text-5xl font-bold leading-tight mb-4">{post.title}</h1>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getCategoryColor(post.category)}`}>
                  {post.category.charAt(0).toUpperCase() + post.category.slice(1).replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Header Section (when no hero image) */}
        {(!post.images || post.images.length === 0) && (
          <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                  {post.user_avatar ? (
                    <img
                      src={post.user_avatar}
                      alt={post.user_name}
                      className="w-16 h-16 rounded-2xl object-cover"
                    />
                  ) : (
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{post.user_name || 'Unknown User'}</h2>
                  <p className="text-gray-600">{formatDate(post.created_at)}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getCategoryColor(post.category)}`}>
                {post.category.charAt(0).toUpperCase() + post.category.slice(1).replace('_', ' ')}
              </span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 leading-tight">{post.title}</h1>
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-8 border-b border-gray-100">
          <div className="flex flex-wrap gap-4">
            {isOwner ? (
              <>
                <button
                  onClick={onEdit}
                  className="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <svg className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Post
                </button>
                <button
                  onClick={onDelete}
                  className="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <svg className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Post
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onLike}
                  className="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-medium rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <svg className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Like ({post.likes_count || 0})
                </button>
                <button
                  onClick={onShare}
                  className="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium rounded-xl hover:from-emerald-600 hover:to-green-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <svg className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share ({post.shares_count || 0})
                </button>
              </>
            )}
          </div>
        </div>

        {/* Post Content */}
        <div className="p-8">
          <div className="prose prose-xl max-w-none">
            <div 
              className="text-gray-800 leading-relaxed text-lg bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 shadow-inner rich-text-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHTMLForDisplay(post.content || '') }}
            />
          </div>
        </div>

        {/* Image Gallery */}
        {post.images && post.images.length > 0 && (
          <div className="px-8 pb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Gallery ({post.images.length} images)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {post.images.map((image, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
                  onClick={() => openImageModal(index)}
                >
                  <div className="aspect-square">
                    <img
                      src={image.original_image}
                      alt={`Post image ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-sm font-medium">Click to view full size</p>
                  </div>
                  {/* Image number badge */}
                  <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comments Section */}
        <div className="border-t border-gray-100 bg-gradient-to-br from-gray-50 to-white">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-3xl font-bold text-gray-900">
                Comments ({post.comments_count || 0})
              </h3>
              <button
                onClick={() => setShowCommentForm(!showCommentForm)}
                className="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <svg className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {showCommentForm ? 'Cancel' : 'Add Comment'}
              </button>
            </div>

            {/* Comment Form */}
            {showCommentForm && (
              <div className="mb-8 p-6 bg-white rounded-2xl border border-gray-200 shadow-lg">
                <form onSubmit={onSubmitComment}>
                  <div className="mb-6">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts on this post..."
                      className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-lg"
                      rows={4}
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowCommentForm(false)}
                      className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sendingComment}
                      className="group relative inline-flex items-center px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                      {sendingComment ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-6">
              {commentsLoading ? (
                <p className="text-gray-500 text-center py-6">Loading comments...</p>
              ) : comments && comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.id} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="font-bold text-gray-900 text-lg">{comment.user_name || 'Anonymous'}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">No comments yet</h4>
                  <p className="text-gray-500 text-lg">Be the first to share your thoughts on this post!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {openIndex !== null && gallerySlides.length > 0 && (
        <ImageLightboxModal
          isOpen
          images={gallerySlides}
          startIndex={openIndex}
          onClose={closeImageModal}
        />
      )}
    </article>
  );
};

export default PostDetailView;